import React from 'react';
import { isNodeSelection } from '@tiptap/react';
import { Editor } from '@tiptap/react';
import { useRichTextMenuNavigation } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-menu-navigation';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { RichTextBanIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/ban-icon';
import { RichTextHighlighterIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/highlighter-icon';
import { richTextIsMarkInSchema } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextPopover } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/popover/popover';
import { RichTextPopoverTrigger } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/popover/popover';
import { RichTextPopoverContent } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/popover/popover';
import { RichTextSeparator } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/separator/separator';
import { RichTextColorHighlightButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/color-highlight-button/color-highlight-button';
import { richTextCanToggleHighlight } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/color-highlight-button/color-highlight-button';
// --- Styles ---
import 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/color-highlight-popover/color-highlight-popover.scss';

export interface RichTextColorHighlightPopoverColor {
  label: string;
  value: string;
  border?: string;
}

export interface RichTextColorHighlightPopoverContentProps {
  editor?: Editor | null;
  colors?: RichTextColorHighlightPopoverColor[];
  onClose?: () => void;
}

export interface RichTextColorHighlightPopoverProps
  extends Omit<RichTextButtonProps, 'type'> {
  /** The TipTap editor instance. */
  editor?: Editor | null;
  /** The highlight colors to display in the popover. */
  colors?: RichTextColorHighlightPopoverColor[];
  /** Whether to hide the highlight popover when unavailable. */
  hideWhenUnavailable?: boolean;
}

export const RICH_TEXT_DEFAULT_HIGHLIGHT_COLORS: RichTextColorHighlightPopoverColor[] =
  [
    {
      label: 'Green',
      value: 'var(--tt-color-highlight-green)',
      border: 'var(--tt-color-highlight-green-contrast)',
    },
    {
      label: 'Blue',
      value: 'var(--tt-color-highlight-blue)',
      border: 'var(--tt-color-highlight-blue-contrast)',
    },
    {
      label: 'Red',
      value: 'var(--tt-color-highlight-red)',
      border: 'var(--tt-color-highlight-red-contrast)',
    },
    {
      label: 'Purple',
      value: 'var(--tt-color-highlight-purple)',
      border: 'var(--tt-color-highlight-purple-contrast)',
    },
    {
      label: 'Yellow',
      value: 'var(--tt-color-highlight-yellow)',
      border: 'var(--tt-color-highlight-yellow-contrast)',
    },
  ];

export const RichTextColorHighlightPopoverButton = React.forwardRef<
  HTMLButtonElement,
  RichTextButtonProps
>(({ className, children, ...props }, ref) => (
  <RichTextButton
    type="button"
    className={className}
    data-style="ghost"
    data-appearance="default"
    role="button"
    tabIndex={-1}
    aria-label="Highlight text"
    tooltip="Highlight"
    ref={ref}
    {...props}
  >
    {children || <RichTextHighlighterIcon className="tiptap-button-icon" />}
  </RichTextButton>
));

RichTextColorHighlightPopoverButton.displayName = 'ColorHighlightPopoverButton';

export function RichTextColorHighlightPopoverContent({
  editor: providedEditor,
  colors = RICH_TEXT_DEFAULT_HIGHLIGHT_COLORS,
  onClose,
}: RichTextColorHighlightPopoverContentProps) {
  const editor = useRichTextTiptapEditor(providedEditor);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const removeHighlight = React.useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetMark('highlight').run();
    onClose?.();
  }, [editor, onClose]);

  const menuItems = React.useMemo(
    () => [...colors, { label: 'Remove highlight', value: 'none' }],
    [colors],
  );

  const { selectedIndex } = useRichTextMenuNavigation({
    containerRef,
    items: menuItems,
    orientation: 'both',
    onSelect: (item) => {
      if (item.value === 'none') {
        removeHighlight();
      }
      onClose?.();
    },
    onClose,
    autoSelectFirstItem: false,
  });

  return (
    <div
      ref={containerRef}
      className="tiptap-color-highlight-content"
      tabIndex={0}
    >
      <div className="tiptap-button-group" data-orientation="horizontal">
        {colors.map((color, index) => (
          <RichTextColorHighlightButton
            key={color.value}
            editor={editor}
            color={color.value}
            aria-label={`${color.label} highlight color`}
            tabIndex={index === selectedIndex ? 0 : -1}
            data-highlighted={selectedIndex === index}
            onClick={onClose}
          />
        ))}
      </div>

      <RichTextSeparator />

      <div className="tiptap-button-group">
        <RichTextButton
          onClick={removeHighlight}
          aria-label="Remove highlight"
          tabIndex={selectedIndex === colors.length ? 0 : -1}
          type="button"
          role="menuitem"
          data-style="ghost"
          data-highlighted={selectedIndex === colors.length}
        >
          <RichTextBanIcon className="tiptap-button-icon" />
        </RichTextButton>
      </div>
    </div>
  );
}

export function RichTextColorHighlightPopover({
  editor: providedEditor,
  colors = RICH_TEXT_DEFAULT_HIGHLIGHT_COLORS,
  hideWhenUnavailable = false,
  ...props
}: RichTextColorHighlightPopoverProps) {
  const editor = useRichTextTiptapEditor(providedEditor);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(false);

  const markAvailable = richTextIsMarkInSchema('highlight', editor);

  React.useEffect(() => {
    if (!editor) return;

    const updateIsDisabled = () => {
      let isDisabled = false;

      if (!markAvailable || !editor) {
        isDisabled = true;
      }

      const isInCompatibleContext =
        editor.isActive('code') ||
        editor.isActive('codeBlock') ||
        editor.isActive('imageUpload');

      if (isInCompatibleContext) {
        isDisabled = true;
      }

      setIsDisabled(isDisabled);
    };

    editor.on('selectionUpdate', updateIsDisabled);
    editor.on('update', updateIsDisabled);

    return () => {
      editor.off('selectionUpdate', updateIsDisabled);
      editor.off('update', updateIsDisabled);
    };
  }, [editor, markAvailable]);

  const isActive = editor?.isActive('highlight') ?? false;

  const shouldShow = React.useMemo(() => {
    if (!hideWhenUnavailable || !editor) return true;

    return !(
      isNodeSelection(editor.state.selection) ||
      !richTextCanToggleHighlight(editor)
    );
  }, [hideWhenUnavailable, editor]);

  if (!shouldShow || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <RichTextPopover open={isOpen} onOpenChange={setIsOpen}>
      <RichTextPopoverTrigger asChild>
        <RichTextColorHighlightPopoverButton
          disabled={isDisabled}
          data-active-state={isActive ? 'on' : 'off'}
          data-disabled={isDisabled}
          aria-pressed={isActive}
          {...props}
        />
      </RichTextPopoverTrigger>

      <RichTextPopoverContent aria-label="Highlight colors">
        <RichTextColorHighlightPopoverContent
          editor={editor}
          colors={colors}
          onClose={() => setIsOpen(false)}
        />
      </RichTextPopoverContent>
    </RichTextPopover>
  );
}

export default RichTextColorHighlightPopover;
